import { useEffect, useState, useRef } from "react";
import "../../../../../css/CashflowModeller2/Inputs/Inputs.css";
import "../../../../../css/CashflowModeller2/Inputs/Input1Big.css";

/** Keep only digits, one dot, optional leading minus */
const sanitizeNumeric = (s) => (s ?? "").replace(/[^0-9.-]/g, "");

/** Format integer part with commas, keep decimals/trailing "." exactly */
const formatWithCommasKeepDecimals = (raw) => {
  if (raw === "" || raw === "-" || raw === "." || raw === "-.") return raw;
  const m = raw.match(/^(-?)(\d*)(\.?)(\d*)$/);
  if (!m) return raw;
  const [, sign, intPart = "", dot = "", decPart = ""] = m;
  const intWithCommas =
    intPart === "" ? "" : intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}${intWithCommas}${dot}${decPart}`;
};

const Input1Big = ({
  isDisabled,
  type = "text", // "text" | "number" | "customDate"
  label,
  value,
  currency = false,
  onChange,
  readOnly,
  isError,
  placeholderText,
}) => {
  const [status, setStatus] = useState("default");
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  // Raw numeric string we pass to parent (no commas/symbols)
  const [rawEditing, setRawEditing] = useState(() =>
    sanitizeNumeric(value?.toString?.() || "")
  );

  // What we display in the input (commas while typing)
  const [internalValue, setInternalValue] = useState(() => {
    if (type !== "number") return value?.toString?.() || "";
    return formatWithCommasKeepDecimals(rawEditing);
  });

  // Sync external value -> internal (don't clobber mid-typing)
  useEffect(() => {
    const s = value?.toString?.() || "";
    if (type !== "number") {
      if (!isFocused) setInternalValue(s);
      return;
    }
    if (isFocused) return; // leave user alone while editing
    const raw = sanitizeNumeric(s);
    setRawEditing(raw);
    setInternalValue(formatWithCommasKeepDecimals(raw));
  }, [value, type, isFocused]);

  const handleLocalChange = (e) => {
    const input = e.target.value;

    if (type === "number") {
      const raw = sanitizeNumeric(input);
      // allow optional leading minus and a single dot
      const numericRegex = /^-?\d*\.?\d*$/;
      if (!numericRegex.test(raw)) return;

      setRawEditing(raw);
      setInternalValue(formatWithCommasKeepDecimals(raw));
      onChange?.(raw); // parent gets clean raw string
      return;
    }

    // text/customDate
    setInternalValue(input);
    onChange?.(input);
  };

  const handleFocus = () => {
    if (readOnly) return;
    setStatus("selected");
    setIsFocused(true);
  };

  const handleBlur = () => {
    setStatus("default");
    setIsFocused(false);
  };

  const getDisplayValue = () => {
    if (type === "text" || type === "customDate") return internalValue || "";
    if (!internalValue) return "";
    // Add £ only when not focused
    return !isFocused && currency ? `£${internalValue}` : internalValue;
  };

  return (
    <div className={`${label ? "input-label-container" : ""}`}>
      {label && <div className="Label">{label}</div>}

      <div
        className={`input-container ${isDisabled ? "disabled" : ""} ${status} ${
          readOnly ? "readOnly" : ""
        } ${isError ? "error" : ""}`}
      >
        <input
          ref={inputRef}
          type={type === "customDate" ? "number" : "text"}
          inputMode={type === "number" ? "decimal" : "text"}
          pattern={type === "number" ? "-?\\d*\\.?\\d*" : undefined}
          value={getDisplayValue()}
          onChange={handleLocalChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className="input-field Input input1-big"
          placeholder={
            placeholderText
              ? placeholderText
              : type === "number"
              ? currency
                ? "£18,000.00"
                : "18,000.00"
              : type === "customDate"
              ? "YYYY"
              : "7iM"
          }
          disabled={isDisabled}
          readOnly={readOnly}
        />
      </div>
    </div>
  );
};

export default Input1Big;
